/**
 * Converts a Dropbox share URL to a direct download URL
 * Changes dl=0 to dl=1 to trigger automatic download
 * 
 * @param {string} shareUrl - The Dropbox share URL
 * @returns {string} - The download URL
 */
export const convertToDownloadUrl = (shareUrl) => {
  if (!shareUrl || typeof shareUrl !== 'string') {
    console.warn('Invalid share URL provided to convertToDownloadUrl:', shareUrl);
    return shareUrl;
  }

  // Handle different Dropbox URL formats
  if (shareUrl.includes('dl=0')) {
    return shareUrl.replace('dl=0', 'dl=1');
  } else if (shareUrl.includes('dl=1')) {
    // Already a download URL
    return shareUrl;
  } else {
    // URL doesn't have dl parameter, add it
    const separator = shareUrl.includes('?') ? '&' : '?';
    return `${shareUrl}${separator}dl=1`;
  }
};

/**
 * Converts a Dropbox share URL to a raw content URL
 * Changes dl=0 to raw=1 for direct file access
 * 
 * @param {string} shareUrl - The Dropbox share URL
 * @returns {string} - The raw content URL
 */
export const convertToRawUrl = (shareUrl) => {
  if (!shareUrl || typeof shareUrl !== 'string') {
    console.warn('Invalid share URL provided to convertToRawUrl:', shareUrl);
    return shareUrl;
  }

  if (shareUrl.includes('dl=0')) {
    return shareUrl.replace('dl=0', 'raw=1');
  } else if (shareUrl.includes('dl=1')) {
    return shareUrl.replace('dl=1', 'raw=1');
  } else {
    // URL doesn't have dl parameter, add raw=1
    const separator = shareUrl.includes('?') ? '&' : '?';
    return `${shareUrl}${separator}raw=1`;
  }
};

/**
 * Validates if a URL is a valid Dropbox share URL
 * 
 * @param {string} url - The URL to validate
 * @returns {boolean} - Whether the URL is a valid Dropbox share URL
 */
export const isValidDropboxUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const dropboxPatterns = [
    /^https:\/\/www\.dropbox\.com\/s\//,
    /^https:\/\/www\.dropbox\.com\/sh\//,
    /^https:\/\/www\.dropbox\.com\/scl\//,
    /^https:\/\/dropbox\.com\/s\//,
    /^https:\/\/dropbox\.com\/sh\//,
    /^https:\/\/dropbox\.com\/scl\//,
  ];

  return dropboxPatterns.some(pattern => pattern.test(url));
};

/**
 * Extracts folder information from a Dropbox path
 * 
 * @param {string} path - The Dropbox folder path
 * @returns {object} - Object with folder info
 */
export const parseFolderPath = (path) => {
  if (!path || typeof path !== 'string') {
    return { client: '', jobNumber: '', folderName: '' };
  }

  const parts = path.split('/').filter(part => part.length > 0);
  
  if (parts.length < 3) {
    return { client: '', jobNumber: '', folderName: '' };
  }

  return {
    client: parts[0],
    jobNumber: parts[1],
    folderName: parts[parts.length - 1],
    fullPath: path,
  };
};

/**
 * Handles download with error tracking
 * 
 * @param {string} shareUrl - The Dropbox share URL
 * @param {string} folderName - Name of the folder being downloaded
 */
export const initiateDownload = (shareUrl, folderName = 'folder') => {
  try {
    if (!isValidDropboxUrl(shareUrl)) {
      throw new Error('Invalid Dropbox URL');
    }

    const downloadUrl = convertToDownloadUrl(shareUrl);
    
    // Open in new tab to prevent popup blockers
    const downloadWindow = window.open(downloadUrl, '_blank');
    
    if (!downloadWindow) {
      // Fallback if popup was blocked
      window.location.href = downloadUrl;
    }

    // Track download attempt (could be extended with analytics)
    console.log(`Download initiated for ${folderName}:`, downloadUrl);
    
  } catch (error) {
    console.error('Download failed:', error);
    throw new Error(`Failed to download ${folderName}. Please try again.`);
  }
};

/**
 * Estimates folder size limitations
 * Dropbox has limits of 20GB and 10,000 files per download
 * 
 * @param {number} fileCount - Number of files in folder
 * @returns {object} - Information about download limitations
 */
export const checkDownloadLimitations = (fileCount) => {
  const maxFiles = 10000;
  const maxSizeGB = 20;

  return {
    withinFileLimit: fileCount <= maxFiles,
    fileCount,
    maxFiles,
    maxSizeGB,
    warningMessage: fileCount > maxFiles 
      ? `This folder contains ${fileCount} files, which exceeds Dropbox's download limit of ${maxFiles} files. The download may be split into multiple parts.`
      : null,
  };
};